import type { ProductType } from "@constants/prices";

export type SupportedCurrency = "usd" | "cad" | "aud";

export const CURRENCY_CONFIG = {
  usd: {
    symbol: "$",
    label: "USD",
    fullLabel: "United States",
    locale: "en-US",
    flag: "🇺🇸",
  },
  cad: {
    symbol: "CA$",
    label: "CAD",
    fullLabel: "Canada",
    locale: "en-CA",
    flag: "🇨🇦",
  },
  aud: {
    symbol: "AU$",
    label: "AUD",
    fullLabel: "Australia",
    locale: "en-AU",
    flag: "🇦🇺",
  },
} as const;

/**
 * Format a price amount with the appropriate currency symbol
 * @param amount - The numeric amount to format
 * @param currency - The currency code (usd, cad, aud)
 * @returns Formatted currency string (e.g., "$1,399.00", "CA$2,549.00")
 */
export const formatCurrency = (amount: number, currency: SupportedCurrency): string => {
  const config = CURRENCY_CONFIG[currency];
  return `${config.symbol}${amount.toLocaleString(config.locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * Get the price from a product for a specific currency
 * @param product - The product object from PRODUCTS constant
 * @param field - Which price field to access ('price' or 'immediateChargePrice')
 * @param currency - The currency code (usd, cad, aud)
 * @returns The numeric price value for that currency
 */
export const getPrice = (
  product: ProductType,
  field: "price" | "immediateChargePrice",
  currency: SupportedCurrency,
): number => {
  return product[field][currency];
};

/**
 * Get the discount amount for a specific currency
 * @param discountAmount - The discount amount as a currency object
 * @param currency - The currency code (usd, cad, aud)
 * @returns The numeric discount value for that currency
 */
export const getDiscountAmount = (
  discountAmount: { aud: number; cad: number; usd: number },
  currency: SupportedCurrency,
): number => {
  return discountAmount[currency];
};

/**
 * Parse currency from URL parameter
 * @param searchParams - URLSearchParams object
 * @returns Valid currency or default 'usd'
 */
export const parseCurrencyFromURL = (searchParams: URLSearchParams): SupportedCurrency => {
  const currencyParam = searchParams.get("currency")?.toLowerCase();

  if (currencyParam === "cad" || currencyParam === "aud" || currencyParam === "usd") {
    return currencyParam;
  }

  return "usd";
};

/**
 * Validate if a string is a valid currency code
 * @param value - String to validate
 * @returns True if valid currency code
 */
export const isValidCurrency = (value: string): value is SupportedCurrency => {
  return value === "usd" || value === "cad" || value === "aud";
};

/**
 * Update the URL with the selected currency (for bookmarking/sharing)
 * @param currency - The currency to set in the URL
 */
export const updateURLWithCurrency = (currency: SupportedCurrency): void => {
  const url = new URL(window.location.href);
  url.searchParams.set("currency", currency);
  window.history.replaceState({}, "", url);
};
